/**
 * SidebarQuotaMeters — Story 0.2 (v2-Relaunch, Welle 2)
 *
 * Zwei getrennte Kontingent-Meter im Sidebar-Footer (Mobile + IoT) gemaess
 * dem finalen Design-Prototypen (claude-design-import/assets/layout.js,
 * Sidebar-Footer). Datenquelle ist das additive Feld `hotspot_quota` aus
 * GET /api/v2/account/me, das derselbe Backend-HotspotQuotaEnforcer erzeugt,
 * der die Hotspot-Anlage blockt — Anzeige und Enforcement koennen daher nicht
 * auseinanderlaufen.
 *
 * `limit: null` bedeutet unbegrenzt: dann wird bewusst KEIN Fuellbalken
 * gerendert (ein Prozentwert waere sinnlos), sondern nur der Verbrauch.
 */
import { useTranslation } from 'react-i18next'
import { useAccount } from '../features/settings/useAccount'
import type { HotspotQuota, HotspotQuotaBucket } from '../features/settings/useAccount'

/** Ab diesem Fuellgrad wird gewarnt (gelb), ab 100 % rot. */
const WARN_THRESHOLD_PCT = 80

type MeterProps = {
  /** Bucket-Schluessel — steuert testid, Tonalitaet und Label-Uebersetzung. */
  bucket: 'mobile' | 'iot'
  data: HotspotQuotaBucket
}

function fillPercent(used: number, limit: number | null): number {
  if (limit == null) return 0
  // Welle-2-Review F3: Limit 0 (Tarif ohne Kontingent) ist KEINE Division —
  // aber auch kein leerer Balken: wer bei Limit 0 schon Hotspots hat (z.B. nach
  // einem Downgrade), ist zu 100 % ausgeschoepft und muss das rot sehen.
  // Vorher rechnete diese Funktion 0 % und der Balken blieb markenfarben leer.
  if (limit <= 0) return used > 0 ? 100 : 0
  const pct = Math.min(100, Math.max(0, (used / limit) * 100))
  // Auf 2 Nachkommastellen runden — 14/25 ergibt sonst 56.00000000000001 %
  // und damit einen unnoetig rauschenden style-Wert.
  return Math.round(pct * 100) / 100
}

function toneFor(bucket: 'mobile' | 'iot', pct: number, unlimited: boolean): string {
  if (unlimited) return bucket === 'mobile' ? 'tone-brand' : 'tone-iot'
  if (pct >= 100) return 'tone-danger'
  if (pct >= WARN_THRESHOLD_PCT) return 'tone-warning'
  return bucket === 'mobile' ? 'tone-brand' : 'tone-iot'
}

function QuotaMeter({ bucket, data }: MeterProps) {
  const { t } = useTranslation('shell')
  const used = data.used ?? 0
  const limit = data.limit
  const unlimited = limit == null
  const pct = fillPercent(used, limit)
  const label = t(`sidebar.quota.${bucket}`)

  // Dynamischer Tooltip (AC-3): Rest, Ausschoepfung oder Unbegrenztheit.
  const tooltip =
    (unlimited
      ? t('sidebar.quota.tooltipUnlimited', { label, used })
      : used >= (limit as number)
        ? t('sidebar.quota.tooltipFull', { label, used, limit })
        : t('sidebar.quota.tooltip', {
            label,
            used,
            limit,
            remaining: (limit as number) - used,
          })) +
    // Issue #44/#67: Hotspots ohne user_hotspots-Zeile blockieren, bevor sie
    // überhaupt als "used" zählen — sonst bleibt diese Zahl unsichtbar.
    (data.blocked ? t('sidebar.quota.tooltipBlocked', { count: data.blocked }) : '')

  // Bei Limit 0 mit bereits vorhandenen Hotspots waere valuemax < valuenow —
  // deshalb auf den tatsaechlichen Verbrauch anheben.
  const ariaMax = unlimited ? undefined : Math.max(limit as number, used)

  const valueText = unlimited
    ? `${used}/${t('sidebar.quota.unlimitedShort')}`
    : `${used}/${limit}`

  return (
    <div
      className="sidebar-quota__row"
      data-testid={`sidebar-quota-${bucket}`}
      title={tooltip}
    >
      <div className="sidebar-quota__head">
        <span>{label}</span>
        <span className="mono sidebar-quota__value" data-testid={`sidebar-quota-${bucket}-value`}>
          {valueText}
        </span>
      </div>
      <div
        className="meter sidebar-quota__meter"
        role="progressbar"
        aria-label={tooltip}
        aria-valuemin={0}
        aria-valuemax={ariaMax}
        aria-valuenow={used}
        aria-valuetext={tooltip}
        data-testid={`sidebar-quota-${bucket}-bar`}
        data-unlimited={unlimited ? 'true' : 'false'}
      >
        {!unlimited && (
          <div
            className={`meter-fill ${toneFor(bucket, pct, unlimited)}`}
            style={{ width: `${pct}%` }}
            data-testid={`sidebar-quota-${bucket}-fill`}
          />
        )}
      </div>
    </div>
  )
}

/**
 * Praesentationsebene — ohne Datenabruf, damit sie isoliert testbar ist.
 */
export function QuotaMeters({ quota }: { quota: HotspotQuota }) {
  const { t } = useTranslation('shell')

  return (
    <div
      className="sidebar-quota"
      data-testid="sidebar-quota"
      role="group"
      aria-label={t('sidebar.quota.ariaLabel')}
    >
      <QuotaMeter bucket="mobile" data={quota.mobile} />
      <QuotaMeter bucket="iot" data={quota.iot} />
    </div>
  )
}

/**
 * Container — liest hotspot_quota aus GET /api/v2/account/me.
 * Solange das Feld fehlt (Ladephase oder aelteres Backend) wird nichts
 * gerendert, damit kein leerer Rahmen in der Sidebar steht.
 */
export function SidebarQuotaMeters() {
  const { account } = useAccount()
  const quota = account?.hotspot_quota
  if (!quota?.mobile || !quota?.iot) return null
  return <QuotaMeters quota={quota} />
}

export default SidebarQuotaMeters
